import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCompanyAppointmentUrlSegment } from '../shared/booking/appointments/companyAppointmentRouting';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { saveAs } from 'file-saver';
import FilterPanel from './archives/FilterPanel';
import { formatStatusLabel } from '../utils/helpers';
import { listAppointments } from '../api/appointments';
import { listMeetings } from '../api/meetings/meetings';
import './archives/styles/Archives.css';
import './archives/styles/ArchivesTable.css';
import './archives/styles/ArchivesTabs.css';
import './archives/styles/ArchivesPagination.css';
import { ListRowSkeletonStack, SkeletonBlock } from './loading/skeletons';

/** Statuses that can appear in archives (excludes pending/confirmed). */
const ARCHIVE_STATUSES = ['cancelled', 'completed', 'no show', 'rescheduled'];

const convertToValidDate = (dateValue) => {
  try {
    if (!dateValue) return null;
    
    // If it's already a Date
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    
    // If it's a Firestore Timestamp
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      const date = dateValue.toDate();
      return isNaN(date.getTime()) ? null : date;
    }

    // If it's a timestamp object with seconds and nanoseconds
    if (dateValue?.seconds !== undefined) {
      const date = new Date(dateValue.seconds * 1000);
      return isNaN(date.getTime()) ? null : date;
    }
    
    // If it's a timestamp number
    if (typeof dateValue === 'number') {
      // Check if it's seconds (Unix timestamp) or milliseconds
      const date = new Date(dateValue > 99999999999 ? dateValue : dateValue * 1000);
      return isNaN(date.getTime()) ? null : date;
    }

    // If it's a string, try to parse it
    if (typeof dateValue === 'string') {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  } catch (error) {
    console.error('Error converting date:', error, 'Value:', dateValue);
    return null;
  }
};

const formatDate = (dateField) => {
  if (!dateField) return 'N/A';
  try {
    const validDate = convertToValidDate(dateField);
    if (!validDate) return 'Invalid Date';
    return format(validDate, 'PPp');
  } catch (error) {
    console.error('Error formatting date:', error, dateField);
    return 'Invalid Date';
  }
};

const getStatusConfig = (status) => {
  const statusMap = {
    'cancelled': { bg: '#f44336', color: '#fff' },
    'completed': { bg: '#4caf50', color: '#fff' },
    'no show': { bg: '#9c27b0', color: '#fff' },
    'rescheduled': { bg: '#2196f3', color: '#fff' },
    'pending': { bg: '#ff9800', color: '#fff' },
    'confirmed': { bg: '#2196f3', color: '#fff' },
    'default': { bg: '#1976d2', color: '#fff' }
  };
  return statusMap[status?.toLowerCase()] || statusMap.default;
};

const formatServices = (appointment) => {
  if (!appointment) return 'N/A';

  // Check for serviceTags first (newer format)
  if (appointment.serviceTags && Array.isArray(appointment.serviceTags) && appointment.serviceTags.length > 0) {
    return appointment.serviceTags.map(tag => 
      typeof tag === 'object' && tag !== null ? tag.name : tag
    ).filter(Boolean).join(', ');
  }
  
  // Check for services array next
  if (appointment.services && Array.isArray(appointment.services) && appointment.services.length > 0) {
    return appointment.services.map(service => 
      typeof service === 'object' && service !== null ? service.name : service
    ).filter(Boolean).join(', ');
  }
  
  // Check for single service object
  if (appointment.service) {
    if (typeof appointment.service === 'object' && appointment.service !== null) {
      return appointment.service.name || 'N/A';
    }
    return appointment.service;
  }

  // Check for serviceName
  if (appointment.serviceName) {
    return appointment.serviceName;
  }
  
  // If no service information is found
  return 'N/A';
};

const mapArchivedAppointment = (id, data) => ({
  ...data,
  id,
  shortSlug: data.shortSlug || null,
  dateTime: convertToValidDate(data.dateTime),
  archivedAt: convertToValidDate(data.archivedAt),
  updatedAt: convertToValidDate(data.updatedAt),
  endTime: convertToValidDate(data.endTime),
  createdAt: convertToValidDate(data.createdAt),
});

const mapArchivedMeeting = (id, data) => ({
  id,
  ...data,
  dateTime: convertToValidDate(data.dateTime),
  archivedAt: convertToValidDate(data.archivedAt),
  updatedAt: convertToValidDate(data.updatedAt),
  endTime: convertToValidDate(data.endTime),
});

const formatDataForCSV = (appointments) => {
  const headers = [
    'Client Name',
    'Date',
    'Time',
    'Service',
    'Status',
    'Archived At'
  ].join(',');

  const rows = appointments.map(appointment => {
    const dateTime = convertToValidDate(appointment.dateTime);
    
    return [
      `"${appointment.clientName || ''}"`,
      `"${dateTime ? format(dateTime, 'PP') : 'N/A'}"`,
      `"${dateTime ? format(dateTime, 'p') : 'N/A'}"`,
      `"${formatServices(appointment)}"`,
      `"${appointment.status || ''}"`,
      `"${formatDate(appointment.archivedAt) || formatDate(appointment.updatedAt)}"`
    ].join(',');
  });

  return [headers, ...rows].join('\n');
};

const formatDataForCSVMeetings = (meetings) => {
  const headers = ['Title', 'Client Name', 'Date', 'Time', 'Status', 'Archived At'].join(',');
  const rows = meetings.map((m) => {
    const dateTime = convertToValidDate(m.dateTime);
    return [
      `"${m.title || ''}"`,
      `"${m.clientName || ''}"`,
      `"${dateTime ? format(dateTime, 'PP') : 'N/A'}"`,
      `"${dateTime ? format(dateTime, 'p') : 'N/A'}"`,
      `"${m.status || ''}"`,
      `"${formatDate(m.archivedAt) || formatDate(m.updatedAt)}"`,
    ].join(',');
  });
  return [headers, ...rows].join('\n');
};

const Archives = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [archivedAppointments, setArchivedAppointments] = useState([]);
  const [archivedMeetings, setArchivedMeetings] = useState([]);
  const [appointmentsHasMore, setAppointmentsHasMore] = useState(false);
  const [meetingsHasMore, setMeetingsHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Appointments, 1 = Meetings
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, companyData } = useAuth();
  const companyId = companyData?.id;
  const userId = user?.uid;
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches);
  const [filters, setFilters] = useState({
    dateRange: [null, null],
    services: [],
    statuses: [],
  });
  
  // Add pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Memoize available services and statuses
  const availableServices = useMemo(() => {
    const servicesSet = new Set();
    archivedAppointments.forEach(appointment => {
      const services = formatServices(appointment).split(', ');
      services.forEach(service => servicesSet.add(service));
    });
    return Array.from(servicesSet);
  }, [archivedAppointments]);

  const availableStatuses = useMemo(() => {
    const statusesSet = new Set(ARCHIVE_STATUSES);
    archivedAppointments.forEach(appointment => {
      if (appointment.status) {
        statusesSet.add(appointment.status);
      }
    });
    archivedMeetings.forEach(meeting => {
      if (meeting.status) statusesSet.add(meeting.status);
    });
    return Array.from(statusesSet);
  }, [archivedAppointments, archivedMeetings]);

  const fetchArchivedMeetingsPage = useCallback(async () => {
    if (!companyId) return { items: [], hasMore: false };
    const meetings = await listMeetings({ companyId, archived: true });
    return {
      items: meetings.map((meeting) => mapArchivedMeeting(meeting.id, meeting)),
      hasMore: false,
    };
  }, [companyId]);

  const fetchArchivedAppointmentsPage = useCallback(async () => {
    if (!companyId) return { items: [], hasMore: false };
    const result = await listAppointments({ companyId, archived: true });
    return {
      items: (result.appointments || []).map((appointment) =>
        mapArchivedAppointment(appointment.id, appointment)
      ),
      hasMore: false,
    };
  }, [companyId]);

  const fetchArchivedData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!userId || !companyId) {
        throw new Error('No authenticated user or company data');
      }

      const [appointmentsPage, meetingsPage] = await Promise.all([
        fetchArchivedAppointmentsPage(),
        fetchArchivedMeetingsPage(),
      ]);

      setArchivedAppointments(appointmentsPage.items);
      setAppointmentsHasMore(false);

      setArchivedMeetings(meetingsPage.items);
      setMeetingsHasMore(false);
    } catch (fetchError) {
      console.error('Error fetching archived data:', fetchError);
      setError('Failed to load archives. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId, companyId, fetchArchivedAppointmentsPage, fetchArchivedMeetingsPage]);

  const loadMoreArchives = useCallback(async () => {
    if (loadingMore) return;

    const isMeetingsTab = activeTab === 1;
    const hasMore = isMeetingsTab ? meetingsHasMore : appointmentsHasMore;
    if (!hasMore) return;

    setLoadingMore(true);
    try {
      if (isMeetingsTab) {
        const meetingsPage = await fetchArchivedMeetingsPage();
        setArchivedMeetings(meetingsPage.items);
        setMeetingsHasMore(false);
      } else {
        const appointmentsPage = await fetchArchivedAppointmentsPage();
        setArchivedAppointments(appointmentsPage.items);
        setAppointmentsHasMore(false);
      }
    } catch (loadError) {
      console.error('Error loading more archives:', loadError);
      setError('Failed to load more archives. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  }, [
    activeTab,
    appointmentsHasMore,
    fetchArchivedAppointmentsPage,
    fetchArchivedMeetingsPage,
    loadingMore,
    meetingsHasMore,
  ]);

  useEffect(() => {
    if (userId && companyId) {
      fetchArchivedData();
    }
  }, [userId, companyId, fetchArchivedData]);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 600px)');
    const handle = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, []);

  // Optimize filtering with memoization
  const filteredAppointments = useMemo(() => {
    return archivedAppointments.filter(appointment => {
      const matchesSearch = !searchTerm || 
        appointment.clientName?.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesDateRange = true;
      if (filters.dateRange[0] && filters.dateRange[1]) {
        const appointmentDate = convertToValidDate(appointment.dateTime);
        
        const startDate = new Date(filters.dateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(filters.dateRange[1]);
        endDate.setHours(23, 59, 59, 999);

        matchesDateRange = appointmentDate >= startDate && appointmentDate <= endDate;
      }

      const matchesServices = filters.services.length === 0 || 
        filters.services.some(service => 
          formatServices(appointment).toLowerCase().includes(service.toLowerCase())
        );

      const matchesStatus = filters.statuses.length === 0 || 
        filters.statuses.includes(appointment.status);

      return matchesSearch && matchesDateRange && matchesServices && matchesStatus;
    });
  }, [archivedAppointments, searchTerm, filters]);

  // Get paginated data
  const paginatedAppointments = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAppointments.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAppointments, page, rowsPerPage]);

  const filteredMeetings = useMemo(() => {
    return archivedMeetings.filter((meeting) => {
      const matchesSearch = !searchTerm ||
        (meeting.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (meeting.clientName || '').toLowerCase().includes(searchTerm.toLowerCase());
      let matchesDateRange = true;
      if (filters.dateRange[0] && filters.dateRange[1]) {
        const meetingDate = convertToValidDate(meeting.dateTime);
        if (!meetingDate) matchesDateRange = false;
        else {
          const startDate = new Date(filters.dateRange[0]);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(filters.dateRange[1]);
          endDate.setHours(23, 59, 59, 999);
          matchesDateRange = meetingDate >= startDate && meetingDate <= endDate;
        }
      }
      const matchesStatus = filters.statuses.length === 0 || filters.statuses.includes(meeting.status);
      return matchesSearch && matchesDateRange && matchesStatus;
    });
  }, [archivedMeetings, searchTerm, filters]);

  const paginatedMeetings = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredMeetings.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredMeetings, page, rowsPerPage]);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = async () => {
    try {
      if (activeTab === 1) {
        const csvContent = formatDataForCSVMeetings(filteredMeetings);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const fileName = `archived-meetings-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        saveAs(blob, fileName);
        return;
      }

      const csvContent = formatDataForCSV(filteredAppointments);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const fileName = `archived-appointments-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      saveAs(blob, fileName);
    } catch (exportError) {
      console.error('Error exporting CSV:', exportError);
    }
  };

  const handleRowClick = (appointment) => {
    if (!appointment?.id) return;
    const appointmentSegment = getCompanyAppointmentUrlSegment(appointment);
    navigate(`/app/overview/appointment/${appointmentSegment}?archived=1`, { state: { fromArchive: true } });
  };

  const handleMeetingRowClick = (meeting) => {
    if (!meeting?.id) return;
    const meetingSegment = meeting.shortSlug ?? meeting.id;
    navigate(`/app/overview/meeting/${meetingSegment}?archived=1`, {
      state: { meeting, fromArchive: true },
    });
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: [null, null],
      services: [],
      statuses: [],
    });
    setSearchTerm('');
  };

  if (!userId || !companyId) {
    return <p className="archives-loading-text">Loading user and company data...</p>;
  }

  if (loading) {
    return (
      <div className="archives-page">
        <div className="archives-page__main">
          <div className="archives-filter-tabs-row" style={{ width: '100%' }}>
            <div className="archives-filter-tabs-row__filter">
              <SkeletonBlock width="100%" height={48} />
            </div>
            <nav className="archives-tabs archives-tabs--vertical" aria-hidden="true">
              <SkeletonBlock width={140} height={36} />
              <SkeletonBlock width={120} height={36} />
            </nav>
          </div>
          <div className="app-table-wrap archives-content-box">
            <ListRowSkeletonStack rows={8} variant="upcoming" label="Loading archives" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="archives-page">
        <div className="archives-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="archives-page">
      <div className="archives-page__main">
        <div className="archives-filter-tabs-row" style={{ width: '100%' }}>
          <div className="archives-filter-tabs-row__filter">
              <FilterPanel
                filters={filters}
                setFilters={setFilters}
                availableServices={activeTab === 0 ? availableServices : []}
                availableStatuses={availableStatuses}
                onClearFilters={handleClearFilters}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onExport={handleExportCSV}
                searchPlaceholder={activeTab === 0 ? 'Search by client name...' : 'Search by title or client name...'}
              />
            </div>
            <nav className="archives-tabs archives-tabs--vertical" role="tablist" aria-label="Archive type">
              <ul className="archives-tabs__list">
                <li className="archives-tabs__tab" role="presentation">
                  <button
                    type="button"
                    role="tab"
                    id="archives-tab-0"
                    aria-selected={activeTab === 0}
                    aria-controls="archives-panel-0"
                    className="archives-tabs__btn"
                    onClick={() => { setActiveTab(0); setPage(0); }}
                  >
                    Appointments{' '}
                    <span className="archives-tabs__count">
                      ({archivedAppointments.length}{appointmentsHasMore ? '+' : ''})
                    </span>
                  </button>
                </li>
                <li className="archives-tabs__tab" role="presentation">
                  <button
                    type="button"
                    role="tab"
                    id="archives-tab-1"
                    aria-selected={activeTab === 1}
                    aria-controls="archives-panel-1"
                    className="archives-tabs__btn"
                    onClick={() => { setActiveTab(1); setPage(0); }}
                  >
                    Meetings{' '}
                    <span className="archives-tabs__count">
                      ({archivedMeetings.length}{meetingsHasMore ? '+' : ''})
                    </span>
                  </button>
                </li>
              </ul>
            </nav>
        </div>

        {/* Archives Content */}
        <div className="app-table-wrap archives-content-box">
          {activeTab === 0 ? (
            <div
              id="archives-panel-0"
              role="tabpanel"
              aria-labelledby="archives-tab-0"
            >
            {isMobile ? (
              <div className="archives-cards">
                {paginatedAppointments.map((appointment) => {
                  if (!appointment) return null;
                  const statusConfig = getStatusConfig(appointment.status);
                  const dateTime = appointment.dateTime instanceof Date ?
                    appointment.dateTime :
                    appointment.dateTime?.toDate?.();
                  return (
                    <a
                      key={appointment.id}
                      href="#"
                      className="archives-card"
                      onClick={(e) => { e.preventDefault(); handleRowClick(appointment); }}
                    >
                      <span className="archives-card__title">{appointment.clientName || 'N/A'}</span>
                      <span className="archives-card__meta">{dateTime ? format(dateTime, 'PP') : 'N/A'}</span>
                      <span className="archives-card__meta">{dateTime ? format(dateTime, 'p') : 'N/A'}</span>
                      <span className="archives-card__service">{formatServices(appointment)}</span>
                      <div className="archives-card__footer">
                        <span
                          className="archives-card__chip"
                          style={{ backgroundColor: `${statusConfig.bg}20`, color: statusConfig.bg }}
                        >
                          {appointment.status ? formatStatusLabel(appointment.status) : 'N/A'}
                        </span>
                        <span className="archives-card__date">
                          {formatDate(appointment.archivedAt) || formatDate(appointment.updatedAt)}
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : (
              <div className="archives-table-wrapper">
                <table className="app-data-table archives-table">
                  <thead>
                    <tr>
                      {['Client Name', 'Date', 'Time', 'Service', 'Status', 'Archived At'].map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAppointments.map((appointment) => {
                      if (!appointment) return null;
                      const statusConfig = getStatusConfig(appointment.status);
                      const dateTime = appointment.dateTime instanceof Date ?
                        appointment.dateTime :
                        appointment.dateTime?.toDate?.();
                      return (
                        <tr key={appointment.id} className="is-clickable" onClick={() => handleRowClick(appointment)}>
                          <td className="archives-table__cell--narrow" style={{ fontWeight: 500 }}>{appointment.clientName || 'N/A'}</td>
                          <td>{dateTime ? format(dateTime, 'PP') : 'N/A'}</td>
                          <td>{dateTime ? format(dateTime, 'p') : 'N/A'}</td>
                          <td className="archives-table__cell--narrow">{formatServices(appointment)}</td>
                          <td>
                            <span
                              className="archives-table__chip"
                              style={{ backgroundColor: `${statusConfig.bg}20`, color: statusConfig.bg }}
                            >
                              {appointment.status ? formatStatusLabel(appointment.status) : 'N/A'}
                            </span>
                          </td>
                          <td className="archives-table__cell--muted">
                            {formatDate(appointment.archivedAt) || formatDate(appointment.updatedAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
              </div>
            ) : (
              <div
                id="archives-panel-1"
                role="tabpanel"
                aria-labelledby="archives-tab-1"
              >
              {isMobile ? (
                <div className="archives-cards">
                  {paginatedMeetings.length === 0 ? (
                    <p className="archives-empty-state">There are currently no meetings in the archive.</p>
                  ) : (
                    paginatedMeetings.map((meeting) => {
                    if (!meeting) return null;
                    const statusConfig = getStatusConfig(meeting.status);
                    const dateTime = meeting.dateTime instanceof Date
                      ? meeting.dateTime
                      : meeting.dateTime?.toDate?.();
                    return (
                      <a
                        key={meeting.id}
                        href="#"
                        className="archives-card"
                        onClick={(e) => { e.preventDefault(); handleMeetingRowClick(meeting); }}
                      >
                        <span className="archives-card__title">{meeting.title || 'Meeting'}</span>
                        <span className="archives-card__meta">{meeting.clientName || 'N/A'}</span>
                        <span className="archives-card__meta">
                          {dateTime ? format(dateTime, 'PP') : 'N/A'} · {dateTime ? format(dateTime, 'p') : 'N/A'}
                        </span>
                        <div className="archives-card__footer">
                          <span
                            className="archives-card__chip"
                            style={{ backgroundColor: `${statusConfig.bg}20`, color: statusConfig.bg }}
                          >
                            {meeting.status ? formatStatusLabel(meeting.status) : 'N/A'}
                          </span>
                          <span className="archives-card__date">
                            {formatDate(meeting.archivedAt) || formatDate(meeting.updatedAt)}
                          </span>
                        </div>
                      </a>
                    );
                  })
                  )}
                </div>
              ) : (
                <div className="archives-table-wrapper">
                  {paginatedMeetings.length === 0 ? (
                    <p className="archives-empty-state">There are currently no meetings in the archive.</p>
                  ) : (
                  <table className="app-data-table archives-table">
                    <thead>
                      <tr>
                        {['Title', 'Client Name', 'Date', 'Time', 'Status', 'Archived At'].map((header) => (
                          <th key={header}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedMeetings.map((meeting) => {
                        if (!meeting) return null;
                        const statusConfig = getStatusConfig(meeting.status);
                        const dateTime = meeting.dateTime instanceof Date
                          ? meeting.dateTime
                          : meeting.dateTime?.toDate?.();
                        return (
                          <tr key={meeting.id} className="is-clickable" onClick={() => handleMeetingRowClick(meeting)}>
                            <td className="archives-table__cell--narrow" style={{ fontWeight: 500 }}>{meeting.title || 'Meeting'}</td>
                            <td>{meeting.clientName || 'N/A'}</td>
                            <td>{dateTime ? format(dateTime, 'PP') : 'N/A'}</td>
                            <td>{dateTime ? format(dateTime, 'p') : 'N/A'}</td>
                            <td>
                              <span
                                className="archives-table__chip"
                                style={{ backgroundColor: `${statusConfig.bg}20`, color: statusConfig.bg }}
                              >
                                {meeting.status ? formatStatusLabel(meeting.status) : 'N/A'}
                              </span>
                            </td>
                            <td className="archives-table__cell--muted">
                              {formatDate(meeting.archivedAt) || formatDate(meeting.updatedAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  )}
                </div>
              )}
              </div>
            )}
          </div>

          {/* Pagination Section */}
          {(() => {
            const total = activeTab === 0 ? filteredAppointments.length : filteredMeetings.length;
            const hasMore = activeTab === 0 ? appointmentsHasMore : meetingsHasMore;
            const start = total === 0 ? 0 : Math.min(total, page * rowsPerPage + 1);
            const end = Math.min(total, (page + 1) * rowsPerPage);
            const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
            return (
              <div className="archives-pagination">
                <span className="archives-pagination__summary">
                  Showing {start}-{end} of {total} loaded archives
                  {hasMore ? ' (more available)' : ''}
                </span>
                <div className="archives-pagination__controls">
                  <div className="archives-pagination__rows-wrap">
                    <label htmlFor="archives-rows-per-page" className="archives-pagination__rows-label">
                      Rows per page:
                    </label>
                    <select
                      id="archives-rows-per-page"
                      className="archives-pagination__select"
                      value={rowsPerPage}
                      onChange={handleChangeRowsPerPage}
                      aria-label="Rows per page"
                    >
                      {[10, 25, 50].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <span className="archives-pagination__range">
                    {start}-{end} of {total}
                  </span>
                  <nav className="archives-pagination__nav" aria-label="Pagination">
                    <button
                      type="button"
                      className="archives-pagination__btn"
                      onClick={() => handleChangePage(page - 1)}
                      disabled={page <= 0}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="archives-pagination__btn"
                      onClick={() => handleChangePage(page + 1)}
                      disabled={page >= maxPage}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </nav>
                </div>
                {hasMore && (
                  <button
                    type="button"
                    className="archives-pagination__load-more"
                    onClick={loadMoreArchives}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading more…' : 'Load more archives'}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>
  );
};

export default memo(Archives);
